/**
 * =============================================================================
 * IDS CRM Web - Dashboard world clocks
 * =============================================================================
 *
 * PURPOSE:
 * Header strip of live local times, one chip per wall-clock time across the
 * timezones where the business has active customers/distributors (server:
 * GET /customers/timezones, derived from record coordinates/countries).
 * Chips are labelled by timezone abbreviation (BST, CEST, PDT, ...) — taken
 * live from Intl so DST changes relabel automatically. Zones currently
 * showing the same time collapse into one chip labelled by the zone with
 * the most customers; hover reveals every zone behind a chip.
 *
 * =============================================================================
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './WorldClocks.css';

function WorldClocks() {
  const { authFetch } = useAuth();
  const [zones, setZones] = useState([]);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await authFetch('/api/v1/customers/timezones');
        if (response.ok) {
          const data = await response.json();
          if (!cancelled) setZones(data.data || []);
        }
      } catch (err) {
        // The clock strip is decorative — a failed load just hides it.
      }
    };
    load();
    return () => { cancelled = true; };
  }, [authFetch]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  if (zones.length === 0) return null;

  const formatTime = (timeZone) => {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone, hour: '2-digit', minute: '2-digit', hour12: false
      }).format(now);
    } catch (err) {
      return null;
    }
  };

  const formatDay = (timeZone) => {
    try {
      return new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short' }).format(now);
    } catch (err) {
      return '';
    }
  };

  // "BST" / "CEST" / "PDT" (or a bare "GMT+12" where no abbreviation
  // exists) — live from Intl, so DST changes relabel automatically. Each
  // locale only knows its own region's abbreviations (en-GB says BST but
  // GMT-7 for Los Angeles; en-US says PDT but GMT+1 for London), so try
  // both and keep the first real abbreviation.
  const zoneLabel = (timeZone) => {
    let bareOffset = null;
    for (const locale of ['en-GB', 'en-US']) {
      try {
        const abbreviation = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
          .formatToParts(now)
          .find(part => part.type === 'timeZoneName')?.value;
        if (abbreviation && !/^GMT[+-]/.test(abbreviation)) return abbreviation;
        bareOffset = bareOffset || abbreviation;
      } catch (err) {
        // try the next locale
      }
    }
    return bareOffset || (timeZone.split('/').pop() || timeZone).replace(/_/g, ' ');
  };

  // Comparable wall-clock instant for west-to-east ordering of the chips.
  const wallClockValue = (timeZone) => {
    try {
      return new Date(now.toLocaleString('en-US', { timeZone })).getTime();
    } catch (err) {
      return 0;
    }
  };

  const localDay = new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(now);
  // Chips are keyed by wall-clock time, so the viewer's chip is the one
  // showing their own time — a Dublin viewer lights up the BST chip.
  const localKey = `${localDay} ${new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false
  }).format(now)}`;

  // Server sends heaviest zone first, so the first zone at any given time
  // names the chip.
  const chips = [];
  const byTime = new Map();
  zones.forEach(({ timezone, customer_count }) => {
    const time = formatTime(timezone);
    if (!time) return;
    const day = formatDay(timezone);
    const key = `${day} ${time}`;
    const detail = `${timezone.replace(/_/g, ' ')} — ${customer_count} ${Number(customer_count) === 1 ? 'customer' : 'customers'}`;
    const existing = byTime.get(key);
    if (existing) {
      existing.details.push(detail);
      return;
    }
    const chip = {
      key,
      label: zoneLabel(timezone),
      time,
      day,
      sort: wallClockValue(timezone),
      details: [detail]
    };
    byTime.set(key, chip);
    chips.push(chip);
  });

  chips.sort((a, b) => a.sort - b.sort);

  return (
    <div className="world-clocks" aria-label="Local times where we have active customers">
      {chips.map(chip => (
        <span
          key={chip.key}
          className={`world-clock-chip${chip.key === localKey ? ' world-clock-chip-local' : ''}`}
          title={chip.key === localKey ? `${chip.details.join('\n')}\nYour local time` : chip.details.join('\n')}
        >
          <span className="world-clock-city">{chip.label}</span>
          <span className="world-clock-time">
            {chip.time}
            {chip.day !== localDay && <span className="world-clock-day"> {chip.day}</span>}
          </span>
        </span>
      ))}
    </div>
  );
}

export default WorldClocks;
